import type { ScheduleItem } from '../types/index.js'
import { broadcastDayWindow } from './broadcast-day.js'
import { scheduleItemTitle } from './utils.js'

/**
 * Cases de la journée affichée dont le fichier ne colle pas à la case,
 * calculées depuis la grille en mémoire comme les trous et les chevauchements :
 * la barre qui les annonce disparaît d'elle-même une fois la case corrigée.
 *
 * Deux défauts, constatés ensemble le 10/10/2026 sur la case « Les
 * Détonnantes » de 23:00, qui portait le fichier de « Dans les coulisses –
 * Ets Bobet » depuis deux jours :
 *   - `OTHER_PROGRAM` : le fichier appartient à une autre fiche que la case ;
 *     la régie affiche un titre, l'antenne en diffuse un autre ;
 *   - `SHORT_MEDIA`  : le fichier est plus court que la case (27:19 pour
 *     31:56) ; le reste de la case passe sur l'interlude, ou sur rien.
 *
 * La durée d'un fichier est arrondie à la seconde par ffprobe et celle d'une
 * case est entière : une seconde d'écart est un arrondi, pas un défaut.
 */

/** Écart en deçà duquel case et fichier sont considérés de même durée. */
export const MEDIA_SHORTFALL_TOLERANCE_SEC = 1

/** Une case dans ces états peut encore être corrigée ou va passer à l'antenne. */
const WATCHED_STATUSES = new Set(['SCHEDULED', 'LOADING', 'ON_AIR'])
/**
 * Un direct n'a pas de fichier ; un bloc pub est fait de spots ; une case
 * d'infoservice joue un segment du fichier du jour : aucun n'a une durée de
 * fichier comparable à celle de sa case.
 */
const IGNORED_ITEM_TYPES = new Set(['LIVE', 'AD_BREAK', 'INFOSERVICE'])

export type GridMediaIssueKind = 'OTHER_PROGRAM' | 'SHORT_MEDIA'

export interface GridMediaIssue {
  key:              string
  kind:             GridMediaIssueKind
  itemId:           string
  gridId:           string
  title:            string
  startTime:        string
  fileName:         string | null
  slotDurationSec:  number
  mediaDurationSec: number | null
  /** Temps de case que le fichier ne remplit pas (`SHORT_MEDIA`), sinon 0. */
  shortfallSec:     number
}

export function findGridMediaIssues(items: ScheduleItem[], date: string): GridMediaIssue[] {
  const { from, to } = broadcastDayWindow(date)
  const dayStart = from.getTime()
  const dayEnd = to.getTime()
  const issues: GridMediaIssue[] = []

  for (const item of items) {
    if (!WATCHED_STATUSES.has(item.status) || IGNORED_ITEM_TYPES.has(item.itemType)) continue
    const media = item.mediaAsset
    if (!media) continue
    const startMs = new Date(item.startTime).getTime()
    if (startMs < dayStart || startMs >= dayEnd) continue

    const base = {
      itemId:           item.id,
      gridId:           item.gridId,
      title:            scheduleItemTitle(item, 'Élément') ?? 'Élément',
      startTime:        item.startTime,
      fileName:         media.fileName ?? null,
      slotDurationSec:  item.durationSec,
      mediaDurationSec: media.durationSec ?? null,
    }

    // Le fichier d'une autre émission prime : sa durée ne dit rien de la case.
    if (media.programId && item.programId && media.programId !== item.programId) {
      issues.push({ ...base, key: `${item.id}:OTHER_PROGRAM`, kind: 'OTHER_PROGRAM', shortfallSec: 0 })
      continue
    }

    if (media.durationSec != null && media.durationSec > 0) {
      const shortfallSec = item.durationSec - media.durationSec
      if (shortfallSec > MEDIA_SHORTFALL_TOLERANCE_SEC) {
        issues.push({ ...base, key: `${item.id}:SHORT_MEDIA`, kind: 'SHORT_MEDIA', shortfallSec })
      }
    }
  }

  return issues.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
}
